import { useLocale, type MessageKey } from '../../i18n/locale'
import type { DataSourceConfig, DataSourceId } from '../../types/dataSource'

interface DataSourceCardProps {
  config: DataSourceConfig
  onToggle: (id: DataSourceConfig['id']) => void
  onConfigure: (id: DataSourceConfig['id']) => void
}

const sourceMeta: Record<DataSourceId, { code: string; type: MessageKey; mode: MessageKey; description: MessageKey }> = {
  polymarket: { code: 'POLY', type: 'source.predictionMarket', mode: 'source.staticSync', description: 'source.descPolymarket' },
  kalshi: { code: 'KAL', type: 'source.predictionMarket', mode: 'source.staticSnapshot', description: 'source.descKalshi' },
  'cme-fedwatch': { code: 'CME', type: 'source.financialData', mode: 'source.reserved', description: 'source.descCme' },
  mock: { code: 'MOCK', type: 'source.internalTest', mode: 'source.notPublic', description: 'source.descMock' },
}

export function DataSourceCard({ config, onToggle, onConfigure }: DataSourceCardProps) {
  const { t } = useLocale()
  const meta = sourceMeta[config.id]
  const statusLabel = config.id === 'cme-fedwatch'
    ? config.status === 'connected' ? t('source.connectedNotRequested') : t('source.notConnected')
    : config.enabled ? t('source.syncedData') : t('source.disabled')
  const statusTone = !config.enabled && config.id !== 'cme-fedwatch' ? 'disabled' : config.status
  const description = t(meta.description)

  return (
    <article className={`source-card source-card-${config.id}`}>
      <div className="source-card-body">
        <div className="source-card-topline">
          <div className="source-name-row"><span className="source-icon" aria-hidden="true">{meta.code.slice(0, 2)}</span><div><h3>{config.name}</h3><span className="source-type">{t(meta.type)} / {t(meta.mode)}</span></div></div>
          <span className={`source-status source-status-${statusTone}`}><span className="status-dot" aria-hidden="true" />{statusLabel}</span>
        </div>
        <p className="source-description">{description}</p>
        <dl className="source-facts">
          <div><dt>{t('settings.enablePublicData')}</dt><dd>{config.enabled ? t('source.enabled') : t('source.disabled')}</dd></div>
          <div><dt>{t('source.credentials')}</dt><dd>{config.requiresCredentials ? t('source.needsConfig') : t('source.noConfig')}</dd></div>
          <div><dt>{t('source.reserved')}</dt><dd>{config.id === 'cme-fedwatch' ? t('source.noApiThisRun') : t('source.homeVisible')}</dd></div>
        </dl>
      </div>
      <div className="source-card-actions">
        <label className="switch-label"><span>{t('settings.enablePublicData')}</span><input type="checkbox" checked={config.enabled} onChange={() => onToggle(config.id)} aria-label={t('source.enableStateAria', { source: config.name })} /><span className="switch" aria-hidden="true" /><small>{config.enabled ? t('source.on') : t('source.off')}</small></label>
        {config.supportsAdvancedConfig && <button className="button button-secondary" type="button" onClick={() => onConfigure(config.id)}>{config.requiresCredentials ? t('source.configureConnection') : t('source.advancedSettings')} <span aria-hidden="true">↗</span></button>}
      </div>
    </article>
  )
}
